import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import {
  FileSpreadsheet,
  Plus,
  Loader2,
  Edit,
  Trash2,
  Search,
  Upload,
  Download,
  Eye,
  Check,
  Star
} from "lucide-react";
import { Switch } from "../components/ui/switch";

export default function PlantillasBeneficiosPage() {
  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingPlantilla, setEditingPlantilla] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    is_default: false,
    file: null
  });
  const { getAuthHeaders } = useAuth();

  const fetchPlantillas = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/plantillas-beneficios`, getAuthHeaders());
      setPlantillas(res.data);
    } catch (err) {
      console.error("Error:", err);
      setPlantillas([]);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchPlantillas();
  }, [fetchPlantillas]);

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      is_default: false,
      file: null
    });
    setEditingPlantilla(null);
  };

  const openNewDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (plantilla) => {
    setEditingPlantilla(plantilla);
    setFormData({
      name: plantilla.name,
      description: plantilla.description || "",
      is_default: plantilla.is_default || false,
      file: null
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("El nombre es requerido");
      return;
    }

    if (!editingPlantilla && !formData.file) {
      toast.error("El archivo de plantilla es requerido");
      return;
    }

    setSaving(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("description", formData.description);
      formDataToSend.append("is_default", formData.is_default);
      
      if (formData.file) {
        formDataToSend.append("file", formData.file);
      }

      if (editingPlantilla) {
        await axios.put(`${API}/plantillas-beneficios/${editingPlantilla.id}`, formDataToSend, {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        });
        toast.success("Plantilla actualizada");
      } else {
        await axios.post(`${API}/plantillas-beneficios`, formDataToSend, {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        });
        toast.success("Plantilla creada");
      }
      setShowDialog(false);
      resetForm();
      fetchPlantillas();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const deletePlantilla = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar esta plantilla?")) return;
    try {
      await axios.delete(`${API}/plantillas-beneficios/${id}`, getAuthHeaders());
      toast.success("Plantilla eliminada");
      setPlantillas(plantillas.filter(p => p.id !== id));
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  const setAsDefault = async (plantilla) => {
    try {
      await axios.put(`${API}/plantillas-beneficios/${plantilla.id}/set-default`, {}, getAuthHeaders());
      toast.success("Plantilla establecida como predeterminada");
      fetchPlantillas();
    } catch (err) {
      toast.error("Error al actualizar");
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const filteredPlantillas = plantillas.filter(p =>
    p.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="plantillas-beneficios-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
              Plantillas de Comparativo
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Gestiona las plantillas Excel para presentar comparativos de beneficios
            </p>
          </div>
          <Button 
            onClick={openNewDialog}
            className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
            data-testid="new-plantilla-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Plantilla
          </Button>
        </div>

        {/* Search */}
        <Card className="border-slate-200 dark:border-slate-700 shadow-sm mb-6 dark:bg-slate-800">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Buscar plantilla..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 border-slate-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white"
                data-testid="search-plantillas"
              />
            </div>
          </CardContent>
        </Card>

        {/* Plantillas Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : filteredPlantillas.length === 0 ? (
          <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800">
            <CardContent className="text-center py-12">
              <FileSpreadsheet className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 dark:text-slate-400 mb-4">
                No hay plantillas registradas
              </p>
              <Button onClick={openNewDialog} variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Subir primera plantilla
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPlantillas.map((plantilla) => (
              <Card key={plantilla.id} className={`border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800 hover:shadow-md transition-shadow ${plantilla.is_default ? 'ring-2 ring-[#45E69A]' : ''}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                        <FileSpreadsheet className="w-6 h-6 text-green-600 dark:text-green-400" />
                      </div>
                      <div>
                        <CardTitle className="text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                          {plantilla.name}
                          {plantilla.is_default && (
                            <span className="inline-flex items-center gap-1 text-xs bg-[#45E69A] text-[#0C314C] px-2 py-0.5 rounded-full font-medium">
                              <Star className="w-3 h-3" />
                              Predeterminada
                            </span>
                          )}
                        </CardTitle>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          Creada {formatDate(plantilla.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {plantilla.description && (
                    <p className="text-sm text-slate-600 dark:text-slate-300 mb-4 line-clamp-2">
                      {plantilla.description}
                    </p>
                  )}
                  
                  {plantilla.filename && (
                    <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 mb-4 bg-slate-50 dark:bg-slate-700 rounded px-3 py-2">
                      <FileSpreadsheet className="w-4 h-4" />
                      <span className="truncate">{plantilla.filename}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2 pt-3 border-t border-slate-100 dark:border-slate-700">
                    {!plantilla.is_default && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setAsDefault(plantilla)}
                        className="flex-1 text-xs"
                      >
                        <Star className="w-3 h-3 mr-1" />
                        Predeterminar
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-slate-200 dark:border-slate-600"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(plantilla)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deletePlantilla(plantilla.id)}
                      className="text-slate-400 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold font-['Manrope']">
                {editingPlantilla ? "Editar Plantilla" : "Nueva Plantilla"}
              </DialogTitle>
              <DialogDescription>
                {editingPlantilla ? "Actualiza los datos de la plantilla" : "Sube una nueva plantilla Excel"}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Ej: Plantilla Comparativo Vida"
                  data-testid="plantilla-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Descripción</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Descripción de la plantilla"
                  rows={2}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="file">Archivo Excel {!editingPlantilla && "*"}</Label>
                <div className="border-2 border-dashed border-slate-200 dark:border-slate-600 rounded-lg p-6">
                  <input
                    id="file"
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => setFormData({...formData, file: e.target.files[0]})}
                    className="hidden"
                  />
                  <label htmlFor="file" className="cursor-pointer flex flex-col items-center gap-2">
                    {formData.file ? (
                      <>
                        <Check className="w-10 h-10 text-green-500" />
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{formData.file.name}</span>
                        <span className="text-xs text-slate-400">Haz clic para cambiar</span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-10 h-10 text-slate-400" />
                        <span className="text-sm font-medium text-slate-500">Haz clic para subir</span>
                        <span className="text-xs text-slate-400">Solo archivos Excel (.xlsx, .xls)</span>
                      </>
                    )}
                  </label>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-[#45E69A]/10 rounded-lg border border-[#45E69A]/30">
                <div className="flex items-center gap-3">
                  <Star className="w-5 h-5 text-[#0C314C]" />
                  <div>
                    <Label htmlFor="is_default" className="cursor-pointer text-[#0C314C] font-medium">
                      Establecer como predeterminada
                    </Label>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Esta plantilla se usará por defecto
                    </p>
                  </div>
                </div>
                <Switch
                  id="is_default"
                  checked={formData.is_default}
                  onCheckedChange={(checked) => setFormData({...formData, is_default: checked})}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
                data-testid="save-plantilla-button"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {saving ? "Guardando..." : "Guardar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
